import { useCallback, useEffect, useState } from 'react'

import { KeyboardArrowDown, KeyboardArrowUp, LocalShipping, PersonOutline, ShareOutlined } from '@mui/icons-material'
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined'
import { Box, Chip, Divider, Skeleton, Typography } from '@mui/material'

import Accordion from '../../../../shared/components/Accordion/Accordion.component'
import Avatar from '../../../../shared/components/Avatar/Avatar.component'
import Button from '../../../../shared/components/Button/Button.component'
import ShareLinkModal from '../../../../shared/components/ShareLinkModal/ShareLinkModal.component'
import Stepper from '../../../../shared/components/Stepper/Stepper.component'
import FulfillmentOrderDetailsComponent from '../FulfillmentOrderDetails/FulfillmentOrderDetails.component'
import DropoffDetailsModal from './components/DropoffDetailsModal/DropoffDetailsModal.component'
import PickupDetailsModal from './components/PickupDetailsModal/PickupDetailsModal.component'

import { getFulfillmentOrderDetails } from '../../../../redux/states/fulfillmentOrders/getFulfillmentOrderDetails/getFulfillmentOrderDetails.slice'
import {
  FulfillmentOrderDetails,
  FulfillmentOrderItem
} from '../../../../redux/states/fulfillmentOrders/getFulfillmentOrderDetails/getFulfillmentOrderDetails.types'
import {
  AccordionButtonProps,
  AccordionFooterProps,
  AccordionHeaderProps
} from '../../../../shared/components/Accordion/Accordion.types'
import Modal from '../../../../shared/components/Modal/Modal.functions'
import date, { dateTimeFormats } from '../../../../shared/functions/Date/date.functions'
import { stripPrefixFromId } from '../../../../shared/functions/ID/id.functions'
import logger from '../../../../shared/functions/Logger/logger.functions'
import status from '../../../../shared/functions/Status/status.dictionaries'
import { getStatusColor } from '../../../../shared/functions/Status/status.functions'
import { formatFirstNameWithLastInitial } from '../../../../shared/functions/String/string.functions'
import { useAppDispatch } from '../../../../shared/hooks/redux.hooks'
import useResponsiveness from '../../../../shared/hooks/responsive.hooks'
import { getFirstAndLastName } from '../../../RecipientTracking/components/RecipientCard/recipientCard.functions'
import {
  buildStepperSteps,
  computeActiveStepIndex,
  computeIsLastStepCompleted,
  formatCentsToPrice
} from './FulfillmentOrderAccordion.functions'
import styles from './FulfillmentOrderAccordion.styles'
import { AccordionViewMode, SenderFulfillmentOrder, TravelerViewData } from './FulfillmentOrderAccordion.types'

interface FulfillmentOrderAccordionProps {
  viewMode?: AccordionViewMode
  travelerViewData?: TravelerViewData
  item?: unknown
  index?: number
  listLength?: number
  refreshData?: (onSuccess?: (data?: unknown) => void) => void
}

const FulfillmentOrderAccordion = (props: FulfillmentOrderAccordionProps) => {
  const { viewMode = 'sender', travelerViewData } = props

  const senderFulfillmentOrder = viewMode === 'sender' ? (props.item as unknown as SenderFulfillmentOrder) : null

  const travelerFulfillmentOrder = viewMode === 'traveler' ? travelerViewData?.fulfillmentOrder : null

  const orderId = senderFulfillmentOrder?.orderId || travelerFulfillmentOrder?.orderId || ''

  const orderStatus = senderFulfillmentOrder?.status || travelerFulfillmentOrder?.status
  const statusName = orderStatus?.name?.toLowerCase() || 'pending'

  const [isTrackingExpanded, setIsTrackingExpanded] = useState(false)
  const [isTrackingLoading, setIsTrackingLoading] = useState(false)
  const [isDetailsLoading, setIsDetailsLoading] = useState(viewMode === 'sender')
  const [orderDetails, setOrderDetails] = useState<FulfillmentOrderDetails | null>(null)

  const [isDesktop] = useResponsiveness()
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (viewMode !== 'sender') return

    const fetchOrderDetails = async () => {
      try {
        setIsDetailsLoading(true)

        const response = await dispatch(getFulfillmentOrderDetails({ orderId })).unwrap()

        setOrderDetails(response)
      } catch (error) {
        logger.logError(error, 'Failed to fetch fulfillment order details', 'FulfillmentOrderAccordion')
      } finally {
        setIsDetailsLoading(false)
      }
    }

    fetchOrderDetails()
  }, [dispatch, orderId, viewMode])

  const statusColor = getStatusColor(statusName)
  const statusLabel = orderStatus?.displayName || 'Pending'

  const handleTrackOrder = useCallback(async () => {
    if (isTrackingExpanded) {
      setIsTrackingExpanded(false)

      return
    }

    setIsTrackingExpanded(true)

    if (!orderDetails) {
      setIsTrackingLoading(true)

      try {
        const response = await dispatch(getFulfillmentOrderDetails({ orderId })).unwrap()

        setOrderDetails(response)
      } catch (error) {
        logger.logError(error, 'Failed to fetch fulfillment order tracking details', 'handleTrackOrder')
      } finally {
        setIsTrackingLoading(false)
      }
    }
  }, [dispatch, orderId, isTrackingExpanded, orderDetails])

  const handleShareLink = useCallback(() => {
    const senderIdParam = senderFulfillmentOrder?.senderId || ''
    const shareLink = senderIdParam
      ? `${window.location.origin}/offers?fulfillmentOrderIds=${orderId}&senderId=${senderIdParam}`
      : `${window.location.origin}/fulfillment-orders/${orderId}`
    const shareLinkMessage = `Check out my fulfillment order on Hourrier!`

    Modal.show({
      body: (
        <ShareLinkModal
          shareLink={shareLink}
          shareLinkMessage={shareLinkMessage}
          open={true}
          primaryText="Share Your Order"
          secondaryText="Share this order with travelers who might be able to help."
        />
      ),
      primaryButton: {
        label: 'Close',
        onClick: () => Modal.close()
      }
    })
  }, [orderId, senderFulfillmentOrder?.senderId])

  const handleOrderDetails = useCallback(() => {
    if (!orderDetails) return

    const formattedCreatedDate = date(orderDetails.createdAt).format(dateTimeFormats.date.medium)
    const strippedOrderId = stripPrefixFromId(orderDetails.orderId)

    Modal.show({
      title: 'Order Details',
      subtitle: (
        <Box sx={styles.orderDetailsSubtitleContainer}>
          <Typography sx={styles.orderDetailsSubtitleText}>
            {strippedOrderId} • Created {formattedCreatedDate}
          </Typography>

          <Chip label={statusLabel} size="small" color={statusColor} />
        </Box>
      ),
      body: <FulfillmentOrderDetailsComponent orderDetails={orderDetails} />,
      primaryButton: {
        label: 'Dismiss',
        onClick: () => Modal.close()
      }
    })
  }, [orderDetails, statusLabel, statusColor])

  const handleRecipientDetails = useCallback(() => {
    if (!travelerFulfillmentOrder?.recipient) return

    const { recipient } = travelerFulfillmentOrder
    const hasAddress =
      recipient.streetAddress && recipient.city && recipient.stateProvince && recipient.postalCode && recipient.country

    const fullAddress = hasAddress
      ? [
          recipient.streetAddress,
          `${recipient.city}, ${recipient.stateProvince} ${recipient.postalCode}`,
          recipient.country
        ]
          .filter(Boolean)
          .join('\n')
      : null

    const { firstName: recipientFirstName, lastName: recipientLastName } = getFirstAndLastName(recipient.name)
    const trimmedRecipientFirstName = recipientFirstName.trim()
    const trimmedRecipientLastName = recipientLastName.trim()
    const recipientDisplayName = hasAddress
      ? [trimmedRecipientFirstName, trimmedRecipientLastName].filter(Boolean).join(' ')
      : formatFirstNameWithLastInitial(trimmedRecipientFirstName, trimmedRecipientLastName)

    Modal.show({
      title: 'Recipient Details',
      subtitle: 'Delivery information for this order',
      body: (
        <Box sx={styles.recipientDetailsContainer}>
          <Box sx={styles.recipientDetailRow}>
            <Typography sx={styles.recipientDetailLabelText}>Name</Typography>
            <Typography sx={styles.recipientDetailValueText}>{recipientDisplayName}</Typography>
          </Box>

          <Divider sx={styles.recipientDetailDivider} />

          <Box sx={styles.recipientDetailRow}>
            <Typography sx={styles.recipientDetailLabelText}>Address</Typography>

            <Typography sx={styles.recipientDetailValueText}>
              {fullAddress ?? (
                <>
                  Select <strong>Get Paid</strong> or <strong>Book Flight</strong> to confirm trip and view airport item
                  pick-up details.
                </>
              )}
            </Typography>
          </Box>
        </Box>
      ),
      primaryButton: {
        label: 'Dismiss',
        onClick: () => Modal.close()
      }
    })
  }, [travelerFulfillmentOrder])

  const handlePickupDetails = useCallback(() => {
    if (!travelerFulfillmentOrder) return

    Modal.show({
      title: 'Pickup Details',
      subtitle: 'Your order is ready for collection',
      body: (
        <PickupDetailsModal
          pickupCode={travelerFulfillmentOrder.pickupInstructions?.pickupCode || null}
          pickupCodeExpiresAt={travelerFulfillmentOrder.pickupInstructions?.pickupCodeExpiresAt || null}
          logisticsPartnerLocation={travelerViewData?.logisticsPartnerLocation || null}
          flightDayOfWeek={travelerViewData?.flightDayOfWeek || null}
        />
      ),
      primaryButton: {
        label: 'Got it',
        onClick: () => Modal.close()
      }
    })
  }, [travelerFulfillmentOrder, travelerViewData?.logisticsPartnerLocation, travelerViewData?.flightDayOfWeek])

  const handleDropoffDetails = useCallback(() => {
    if (!travelerFulfillmentOrder) return

    Modal.show({
      title: 'Dropoff Details',
      subtitle: 'Follow these steps to deliver the item',
      body: (
        <DropoffDetailsModal
          uspsLabelUrl={travelerFulfillmentOrder.uspsLabelUrl || null}
          uspsLocationName={travelerFulfillmentOrder.uspsSelectedFacilityName || null}
          uspsLocationAddress={travelerFulfillmentOrder.uspsSelectedLocationAddress || null}
          uspsLocationHours={travelerFulfillmentOrder.uspsDropoffLocationHours || null}
          uspsTrackingNumber={travelerFulfillmentOrder.uspsTrackingNumber || null}
        />
      ),
      primaryButton: {
        label: 'Got it',
        onClick: () => Modal.close()
      }
    })
  }, [travelerFulfillmentOrder])

  const isTravelerPickupPhase =
    statusName === status.fulfillmentOrderStatus.readyForTravelerPickup ||
    statusName === status.fulfillmentOrderStatus.withTraveler

  const isTravelerDropoffPhase = statusName === status.fulfillmentOrderStatus.withTraveler
  const isUsDestination = travelerFulfillmentOrder?.isUsDestination === true

  const getSenderAccordionHeader = (): AccordionHeaderProps => {
    const buttons: AccordionButtonProps[] = []

    const isAtAirport = statusName === 'ready_for_traveler_pickup'

    if (isAtAirport) {
      buttons.push({
        text: isDesktop ? 'Share Link' : undefined,
        buttonType: 'secondary',
        icon: <ShareOutlined />,
        onClick: handleShareLink,
        tooltipText: 'Share this order with potential travelers'
      })
    }

    buttons.push({
      text: isDesktop ? 'Order Details' : undefined,
      buttonType: 'tertiary',
      onClick: handleOrderDetails,
      tooltipText: 'View order details'
    })

    return {
      mainContent: (
        <Box sx={styles.headerContainer}>
          <Typography variant={isDesktop ? 'h6' : 'h5'} sx={styles.accordionHeaderMainContent}>
            Order: {stripPrefixFromId(orderId)}
          </Typography>

          <Chip label={statusLabel} size="small" color={statusColor} />
        </Box>
      ),
      buttons,
      buttonsWidth: null
    }
  }

  const getTravelerAccordionHeader = (): AccordionHeaderProps => {
    const buttons: AccordionButtonProps[] = []

    const isFlightBooked =
      travelerViewData?.flightItineraryStatus === status.flightStatus.flightBooked ||
      travelerViewData?.flightItineraryStatus ===
        status.externalFlightBookingStatus.externalFlightBookingDetailsConfirmed

    const shouldShowPickupDetails =
      isFlightBooked && (statusName === 'ready_for_traveler_pickup' || (isTravelerPickupPhase && !isUsDestination))

    if (shouldShowPickupDetails) {
      buttons.push({
        text: isDesktop ? 'Pickup Details' : undefined,
        buttonType: 'secondary',
        icon: <LocalShipping />,
        onClick: handlePickupDetails,
        tooltipText: 'View pickup instructions and code'
      })
    }

    if (isTravelerDropoffPhase && isUsDestination) {
      buttons.push({
        text: isDesktop ? 'Dropoff Details' : undefined,
        buttonType: 'secondary',
        icon: <LocalShipping />,
        onClick: handleDropoffDetails,
        tooltipText: 'View dropoff instructions and USPS label'
      })
    }

    buttons.push({
      text: isDesktop ? 'Recipient Details' : undefined,
      buttonType: 'tertiary',
      icon: <PersonOutline />,
      onClick: handleRecipientDetails,
      tooltipText: 'View recipient information'
    })

    return {
      mainContent: (
        <Box sx={styles.headerContainer}>
          <Typography variant={isDesktop ? 'h6' : 'h5'} sx={styles.accordionHeaderMainContent}>
            Order: {stripPrefixFromId(orderId)}
          </Typography>

          <Chip label={statusLabel} size="small" color={statusColor} />
        </Box>
      ),
      buttons,
      buttonsWidth: null
    }
  }

  const getAccordionHeader = (): AccordionHeaderProps => {
    if (viewMode === 'traveler') {
      return getTravelerAccordionHeader()
    }

    return getSenderAccordionHeader()
  }

  const renderItemRow = (item: FulfillmentOrderItem) => {
    const hasImage = item.imageUrls && item.imageUrls.length > 0

    return (
      <Box key={item.itemId} sx={styles.itemRow}>
        <Box sx={styles.itemImage}>
          {hasImage ? (
            <Box component="img" src={item.imageUrls[0]} alt={item.description} sx={styles.itemImageContent} />
          ) : (
            <Inventory2Outlined sx={styles.itemImagePlaceholder} />
          )}
        </Box>

        <Box sx={styles.itemInfo}>
          <Typography sx={styles.itemTitle}>{item.description}</Typography>

          <Typography sx={styles.itemMeta}>
            {formatCentsToPrice(
              item.declaredValueCents,
              orderDetails?.currency || senderFulfillmentOrder?.currency || 'USD'
            )}
          </Typography>
        </Box>
      </Box>
    )
  }

  const renderTravelerItemRow = (item: {
    itemId: string
    imageUrls: string[]
    description: string
    weightGrams: number
    declaredValueCents: number
  }) => {
    const hasImage = item.imageUrls && item.imageUrls.length > 0

    return (
      <Box key={item.itemId} sx={styles.itemRow}>
        <Box sx={styles.itemImage}>
          {hasImage ? (
            <Box component="img" src={item.imageUrls[0]} alt={item.description} sx={styles.itemImageContent} />
          ) : (
            <Inventory2Outlined sx={styles.itemImagePlaceholder} />
          )}
        </Box>

        <Box sx={styles.itemInfo}>
          <Typography sx={styles.itemTitle}>{item.description}</Typography>

          <Typography sx={styles.itemMeta}>
            {formatCentsToPrice(item.declaredValueCents, travelerFulfillmentOrder?.currency || 'USD')}
          </Typography>
        </Box>
      </Box>
    )
  }

  const renderItemsLoadingSkeleton = () => (
    <>
      {Array.from({ length: senderFulfillmentOrder?.itemsCount || 1 }).map((_, index) => (
        <Box key={index} sx={styles.itemRow}>
          <Skeleton variant="rounded" width={50} height={50} sx={styles.itemLoadingSkeleton} />

          <Box sx={styles.itemLoadingTextContainer}>
            <Skeleton variant="rounded" height={16} width="70%" />
            <Skeleton variant="rounded" height={12} width="50%" />
          </Box>
        </Box>
      ))}
    </>
  )

  const renderTravelerInfo = () => {
    if (viewMode === 'traveler') return null
    if (!senderFulfillmentOrder?.travelerFirstName || !senderFulfillmentOrder?.travelerLastName) return null

    const travelerFullName = `${senderFulfillmentOrder.travelerFirstName} ${senderFulfillmentOrder.travelerLastName}`

    return (
      <>
        <Divider sx={styles.sectionDivider} />

        <Box sx={styles.travelerDetailsContainer}>
          <Box sx={styles.travelerDetailsWrapper}>
            <Avatar
              firstName={senderFulfillmentOrder.travelerFirstName}
              lastName={senderFulfillmentOrder.travelerLastName}
              size="small"
            />

            <Box sx={styles.travelerDetailsTextContainer}>
              <Typography sx={styles.travelerDetailsName}>{travelerFullName}</Typography>
              <Typography sx={styles.travelerDetailsTag}>Hurrier</Typography>
            </Box>
          </Box>
        </Box>
      </>
    )
  }

  const renderTrackingLoader = () => (
    <Box sx={styles.trackingLoaderContainer}>
      <Skeleton variant="rounded" height={20} width="40%" sx={styles.trackingLoaderTitle} />

      <Box sx={styles.trackingLoaderStepsContainer}>
        {Array.from({ length: 5 }).map((_, index) => (
          <Skeleton key={index} variant="circular" height={28} width={28} />
        ))}
      </Box>

      <Skeleton variant="rounded" height={12} width="100%" sx={styles.trackingLoaderProgress} />
    </Box>
  )

  const renderTrackingDetails = () => {
    if (!isTrackingExpanded) return null

    if (isTrackingLoading) {
      return renderTrackingLoader()
    }

    if (!orderDetails?.trackingSteps || !orderDetails?.trackingDetails) return null

    const stepperSteps = buildStepperSteps(orderDetails.trackingSteps)
    const activeStepIndex = computeActiveStepIndex(orderDetails.trackingSteps, orderDetails.trackingDetails)
    const isLastStepCompleted = computeIsLastStepCompleted(orderDetails.trackingSteps, orderDetails.trackingDetails)

    return (
      <Box sx={styles.trackingDetailsContainer}>
        <Stepper
          activeStep={activeStepIndex}
          steps={stepperSteps}
          noBackground
          isLastStepCompleted={isLastStepCompleted}
          orientation={isDesktop ? 'horizontal' : 'vertical'}
        />
      </Box>
    )
  }

  const getAccordionFooter = (): AccordionFooterProps => {
    return {
      mainContent: (
        <Box sx={styles.footerMainContent}>
          <Box sx={styles.footerContainer}>
            <Typography sx={styles.footerTrackTitle}>Track your order</Typography>

            <Button
              text="Track"
              buttonType="primary"
              icon={isTrackingExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              iconPosition="end"
              onClick={handleTrackOrder}
              loading={isTrackingLoading}
            />
          </Box>

          {renderTrackingDetails()}
        </Box>
      )
    }
  }

  const renderSenderBody = () => (
    <Box sx={styles.itemsContainer}>
      {isDetailsLoading ? renderItemsLoadingSkeleton() : orderDetails?.items.map(renderItemRow)}
      {renderTravelerInfo()}
    </Box>
  )

  const renderTravelerBody = () => {
    if (!travelerFulfillmentOrder) return null

    return <Box sx={styles.itemsContainer}>{travelerFulfillmentOrder.items.map(renderTravelerItemRow)}</Box>
  }

  const renderBody = () => {
    if (viewMode === 'traveler') {
      return renderTravelerBody()
    }

    return renderSenderBody()
  }

  return (
    <Accordion
      header={getAccordionHeader()}
      body={renderBody()}
      footer={getAccordionFooter()}
      noExpandIcon
      noExpandBackgroundColor
      isExpanded
      preventExpandChange
      hasBorder
      hasPadding
    />
  )
}

export default FulfillmentOrderAccordion
